import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import useConfig from "../../../actions/Tenants/config/configHook";
import useNavigateFrom from "../../../utils/useNavigateFrom";
import { useSignalDetail } from "./signalHooks";
import { getCellRelation, Signal } from "./signalTypes";

/** The URL parameter that carries the signal the operator is working (D5). */
export const ACTIVE_SIGNAL_PARAM = "activeSignal";

/**
 * Did the server answer "this signal does not exist"?
 *
 * Only a 4xx-class response says that. A 5xx or a dropped connection says the
 * server could not answer *this time*, which is a different thing entirely:
 * treating it as an answer would let one blip during a page load strip the
 * parameter, show no bar and say nothing — silent, intermittent and close to
 * unreportable. Those keep the guard waiting instead, and the bar appears when
 * a later attempt succeeds.
 *
 * The API client rejects with the raw axios error (constants/apiClient.tsx), so
 * an HTTP failure carries `response.status` and a transport failure carries no
 * `response` at all.
 */
const isNotFound = (error: unknown): boolean => {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return typeof status === "number" && status >= 400 && status < 500;
};

export interface ActiveSignalState {
    /** Set only when the signal is settled AND belongs to the room being viewed. */
    activeSignal?: Signal;
    setActiveSignal: (signalId: string) => void;
    clearActiveSignal: () => void;
}

/**
 * Owns `?activeSignal=<id>` on the ripening-room route (D5).
 *
 * `roomLocationId` is the `FacilityLocation.location_id` of the room being
 * viewed — the same value the route takes as `:location_id` and the same value
 * `signal_relation.facility_location_id` holds, so the two compare directly.
 */
export const useActiveSignal = (roomLocationId?: string): ActiveSignalState => {
    const config = useConfig();
    const [searchParams, setSearchParams] = useSearchParams();

    // Master gate (EFQ-4145): a hand-typed `?activeSignal=` must not show the
    // feature to a tenant without it. Note this is ALSO false during the first
    // render(s) of a hard load, because `root_config` is `{}` until the tenant
    // configs arrive — which is exactly why a falsy gate never clears the
    // param below. "Unknown" and "off" are indistinguishable here.
    const featureEnabled = !!config.root_config?.ripening_room_enable_signals;
    const requestedSignalId = featureEnabled ? searchParams.get(ACTIVE_SIGNAL_PARAM) : null;

    // D7: the detail query is the authoritative source. It is the only one that
    // returns `relations` (for the room match) and `events` (for the conflict
    // notice), and it polls, so someone else's close is noticed without a click.
    const { data: signal, error: detailError } = useSignalDetail(requestedSignalId || undefined);

    const setActiveSignal = useCallback((signalId: string) => {
        // Re-activating the signal that is ALREADY active must not push. A second
        // identical history entry means one Back press lands on the same URL and
        // the banner survives it — exactly what D5 promises it does not. Reachable
        // from the room's Signals tab, whose rows stay clickable while their signal
        // is on the banner, so this is a click away rather than hypothetical.
        if (searchParams.get(ACTIVE_SIGNAL_PARAM) === signalId) return;

        // Push, so the back button clears the banner (D5).
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(ACTIVE_SIGNAL_PARAM, signalId);
            return next;
        });
    }, [searchParams, setSearchParams]);

    const clearActiveSignal = useCallback(() => {
        // Replace, so a completed resolve (or Dismiss) does not leave a history
        // entry that re-opens the banner on Back.
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete(ACTIVE_SIGNAL_PARAM);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // Room-match guard: a stale or hand-edited URL naming a signal from another
    // room clears the param rather than showing a bar about a room the operator
    // is not looking at.
    //
    // Every input to that decision is asynchronous, and deciding before one of
    // them settles kills every deep link on a cold load — only under load,
    // which is the failure class already documented at SignalsPage.tsx:154-160.
    // So we act only once the detail query has ANSWERED and the viewed room is
    // known. Two answers that look like "unsettled" but are not:
    //
    //  - a settled signal with no cell relation genuinely has no room, and does
    //    clear (D4);
    //  - a NOT-FOUND is an answer too. A stale or mistyped id 404s forever, and
    //    leaving the param set would re-request it on every poll and forward it
    //    into onward navigations via getNextRoute — all of it invisible, since
    //    nothing renders. Only 4xx counts; see isNotFound for why a server or
    //    network failure deliberately does not.
    //
    // `configs_loaded` is inert here in production — `root_config` derives only
    // from the fetched configs, so the master gate above is already false
    // whenever it is. It stays as the tripwire for anyone who later makes the
    // flag-off path clear the param, which is exactly the mistake that would
    // resurrect the cold-load bug, and the hook's contract is pinned by a test.
    const signalRoomId = signal ? getCellRelation(signal)?.facility_location_id : undefined;
    const detailAnswered = !!signal || isNotFound(detailError);
    const isSettled = !!config.configs_loaded && detailAnswered && !!roomLocationId;
    const roomMatches = isSettled && signalRoomId === roomLocationId;

    useEffect(() => {
        if (!requestedSignalId || !isSettled || roomMatches) return;
        clearActiveSignal();
    }, [requestedSignalId, isSettled, roomMatches, clearActiveSignal]);

    return {
        activeSignal: roomMatches ? signal : undefined,
        setActiveSignal,
        clearActiveSignal,
    };
};

export default useActiveSignal;

/**
 * WP-4: the shared activation handler. The signals inbox page, the signals
 * inbox modal opened from a room's bell button, and the room's own Signals
 * tab all reduce to the same behaviour — go to this signal's room and make
 * it active there — so there is exactly ONE implementation, used by every
 * `SignalsPage` row click (the page is mounted at `/signals`,
 * `/signals/view/:view`, AND inside the room's `SignalInboxModal`).
 *
 * The room's Signals tab does not go through this: every row there already
 * came from `useSignals({ facility_location_id: cell })`, so it is already
 * IN the signal's room and only needs to set the param in place — see
 * `RipeningRoom/index.tsx`'s `onActivate` passed to `RoomSignalsTab`.
 */
export const useActivateSignal = () => {
    const { navigateTo } = useNavigateFrom();

    return useCallback((signal: Signal, onNoRoom: (signal: Signal) => void) => {
        // D4: a signal with no cell relation has nowhere to point — dev
        // tooling can produce these (see design D3/D4). Navigating anyway
        // would land on `/ripening-room/undefined`; fall back to the detail
        // modal instead.
        const roomId = getCellRelation(signal)?.facility_location_id;
        if (!roomId) {
            onNoRoom(signal);
            return;
        }

        // Invariant 1: `getNextRoute` (useNavigateFrom.tsx) seeds its next
        // query string from the CURRENT one, so the inbox's own
        // `facility_location_id` cell filter would otherwise ride along into
        // the room URL. Passing `null` deletes the key.
        //
        // Invariant 2: `signals` (the `?signals=inbox` modal flag) is
        // dropped in THIS SAME call — not a follow-up effect — so there is
        // never a render where the inbox modal sits over the banner it
        // exists to reveal. It is a harmless no-op when navigating from the
        // standalone `/signals` page, which never sets it.
        //
        // Invariant 7: `roomId` comes from the ROW's own cell relation, not
        // from the room currently being viewed — the room's own inbox modal
        // is only pre-filtered to that room by accident of the route param
        // name, and cross-room selection is reachable via the filter rail.
        navigateTo(`/ripening-room/${roomId}`, false, {
            [ACTIVE_SIGNAL_PARAM]: signal.id,
            facility_location_id: null,
            signals: null,
        });
    }, [navigateTo]);
};
